import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Plus, Wallet } from 'lucide-react';
import type { Account, Transaction } from '@itr/shared';
import { formatMoney, formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent } from '../api/realtime';
import { Badge, Drawer, EmptyState, ErrorState, SkeletonTable, useFetch, useToast } from '../components/ui';

type AccountRow = Account & { customer_name?: string };

export function BillingPage() {
  const { can } = useAuth();
  const [topUpFor, setTopUpFor] = useState<AccountRow | null>(null);

  const accounts = useFetch(() => api.get<AccountRow[]>('/api/plugins/billing/accounts'), [], { refreshMs: 60_000 });
  const summary = useFetch(
    () => api.get<{ revenue: { topupMinor: number; chargedMinor: number; currency: string }; accounts: number }>('/api/billing/summary'),
    [],
  );

  // Balances move with every rated call, so the socket is the only way this page
  // stays honest between polls.
  useRealtimeEvent(['billing'], (event) => {
    if (event.type === 'balance.changed') accounts.reload();
  });

  const rows = accounts.data ?? [];
  const atRisk = rows.filter((a) => !a.postpaid && a.balanceMinor <= a.lowBalanceThresholdMinor);

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Billing</h1>
          <div className="page-subtitle">
            {rows.length} account{rows.length === 1 ? '' : 's'}
            {atRisk.length > 0 && ` · ${atRisk.length} at or below threshold`}
          </div>
        </div>
      </div>

      {summary.data && (
        <div className="stat-strip">
          <div className="stat-chip">
            <div className="stat-chip-label">Consumed · 30 days</div>
            <div className="stat-chip-value">{formatMoney(summary.data.revenue.chargedMinor, summary.data.revenue.currency)}</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Topped up · 30 days</div>
            <div className="stat-chip-value" style={{ color: 'var(--itr-success)' }}>
              {formatMoney(summary.data.revenue.topupMinor, summary.data.revenue.currency)}
            </div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Accounts at risk</div>
            <div className="stat-chip-value" style={{ color: atRisk.length ? 'var(--itr-warning)' : undefined }}>
              {atRisk.length}
            </div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-label">Suspended</div>
            <div className="stat-chip-value" style={{ color: rows.some((a) => a.suspended) ? 'var(--itr-critical)' : undefined }}>
              {rows.filter((a) => a.suspended).length}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {accounts.loading && !accounts.data ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={5} cols={4} />
          </div>
        ) : accounts.error ? (
          <ErrorState error={accounts.error} onRetry={accounts.reload} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<Wallet size={20} />} title="No billing accounts" text="Create a customer, then add a prepaid account for them." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th style={{ width: 150 }}>Account</th>
                  <th data-align="right" style={{ width: 130 }}>
                    Balance
                  </th>
                  <th data-align="right" style={{ width: 120 }}>
                    Threshold
                  </th>
                  <th style={{ width: 110 }}>State</th>
                  <th style={{ width: 110 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => {
                  const low = !account.postpaid && account.balanceMinor <= account.lowBalanceThresholdMinor;
                  return (
                    <tr key={account.id}>
                      <td style={{ fontWeight: 550 }}>{account.customer_name ?? account.name}</td>
                      <td className="subtle" style={{ fontSize: 12.5 }}>
                        {account.name}
                      </td>
                      <td
                        data-align="right"
                        className="nums"
                        style={{
                          fontWeight: 620,
                          color: account.balanceMinor <= 0 ? 'var(--itr-critical)' : low ? 'var(--itr-warning)' : undefined,
                        }}
                      >
                        {formatMoney(account.balanceMinor, account.currency)}
                      </td>
                      <td data-align="right" className="nums subtle">
                        {formatMoney(account.lowBalanceThresholdMinor, account.currency)}
                      </td>
                      <td>
                        {account.suspended ? (
                          <Badge intent="bad">suspended</Badge>
                        ) : account.postpaid ? (
                          <Badge>postpaid</Badge>
                        ) : low ? (
                          <Badge intent="warn">low</Badge>
                        ) : (
                          <Badge intent="good">ok</Badge>
                        )}
                      </td>
                      <td>
                        {can('billing:write') && (
                          <button className="btn btn-sm" onClick={() => setTopUpFor(account)}>
                            <Plus size={13} /> Top up
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={Boolean(topUpFor)} onClose={() => setTopUpFor(null)} title={`Top up ${topUpFor?.customer_name ?? ''}`}>
        {topUpFor && (
          <TopUpForm
            account={topUpFor}
            onDone={() => {
              setTopUpFor(null);
              accounts.reload();
            }}
          />
        )}
      </Drawer>
    </>
  );
}

function TopUpForm({ account, onDone }: { account: AccountRow; onDone: () => void }) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'topup' | 'adjustment' | 'refund'>('topup');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value === 0) return;

    setBusy(true);
    try {
      await api.post(`/api/billing/accounts/${account.id}/transactions`, {
        type,
        // Money crosses the wire in minor units so no float ever touches a balance.
        amountMinor: Math.round(value * 100) * (type === 'refund' ? -1 : 1),
        description: description || `Manual ${type}`,
        // Guards against a double-submit from an impatient click.
        idempotencyKey: `manual:${account.id}:${Date.now()}`,
      });
      toast(`${formatMoney(Math.round(value * 100), account.currency)} posted`, 'good');
      onDone();
    } catch (err) {
      toast('Transaction failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="card" style={{ padding: 14, marginBottom: 18 }}>
        <div className="subtle" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Current balance
        </div>
        <div style={{ fontSize: 26, fontWeight: 660, letterSpacing: '-0.02em', marginTop: 3 }}>
          {formatMoney(account.balanceMinor, account.currency)}
        </div>
      </div>

      <div className="field">
        <label className="field-label">Type</label>
        <select className="select" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
          <option value="topup">Top up (credit)</option>
          <option value="adjustment">Adjustment</option>
          <option value="refund">Refund (debit)</option>
        </select>
      </div>

      <div className="field">
        <label className="field-label">Amount ({account.currency})</label>
        <input
          className="input"
          type="number"
          step="0.01"
          min="0"
          required
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="100.00"
        />
      </div>

      <div className="field">
        <label className="field-label">Reference</label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Bank transfer ref 4821" />
      </div>

      {amount && Number(amount) > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 16, fontSize: 13 }}>
          <div className="row">
            <span className="subtle">New balance</span>
            <span className="grow" />
            <span className="nums" style={{ fontWeight: 620 }}>
              {formatMoney(account.balanceMinor + Math.round(Number(amount) * 100) * (type === 'refund' ? -1 : 1), account.currency)}
            </span>
          </div>
        </div>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Posting…' : 'Post transaction'}
      </button>
    </form>
  );
}

/* ================================================================== */

export function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, reload } = useFetch(
    () => api.get<Account & { transactions: Transaction[] }>(`/api/billing/accounts/${id}`),
    [id],
  );

  if (loading && !data) return <SkeletonTable rows={6} cols={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Account not found" />;

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">{data.name}</h1>
          <div className="page-subtitle">
            {data.postpaid ? 'Postpaid' : 'Prepaid'} · {data.currency}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="subtle" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Balance
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 660,
              letterSpacing: '-0.025em',
              color: data.balanceMinor <= 0 ? 'var(--itr-critical)' : undefined,
            }}
          >
            {formatMoney(data.balanceMinor, data.currency)}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Transactions</span>
        </div>
        {data.transactions.length === 0 ? (
          <EmptyState title="No transactions" text="Charges and top-ups will appear here." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Type</th>
                  <th>Description</th>
                  <th data-align="right" style={{ width: 120 }}>
                    Amount
                  </th>
                  <th data-align="right" style={{ width: 120 }}>
                    Balance
                  </th>
                  <th data-align="right" style={{ width: 130 }}>
                    When
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      <Badge intent={tx.amountMinor >= 0 ? 'good' : 'neutral'}>{tx.type.replace('_', ' ')}</Badge>
                    </td>
                    <td className="truncate" style={{ maxWidth: 320 }}>
                      {tx.description || '—'}
                    </td>
                    <td
                      data-align="right"
                      className="nums"
                      style={{ color: tx.amountMinor >= 0 ? 'var(--itr-success)' : undefined, fontWeight: 570 }}
                    >
                      <span className="row" style={{ gap: 3, justifyContent: 'flex-end' }}>
                        {tx.amountMinor >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {formatMoney(Math.abs(tx.amountMinor), tx.currency)}
                      </span>
                    </td>
                    <td data-align="right" className="nums subtle">
                      {formatMoney(tx.balanceAfterMinor, tx.currency)}
                    </td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {formatRelative(tx.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
